import { useEffect, useState } from "react";
import axiosInstance from "../../api/axios";
import toast from "react-hot-toast";
import {
    Mail,
    Phone,
    UserRound,
    ChevronLeft,
    ChevronRight,
    Check,
    Trash2,
} from "lucide-react";
import ConfirmModal from "../../components/confirm-modal/ConfirmModal";

export default function Messages() {
    const [tickets, setTickets] = useState([]);
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);
    const [total, setTotal] = useState(0);

    const fetchTickets = async (pageNum = 1) => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get("/api/ticket/get-all-admin", {
                params: { pagination: true, page: pageNum, limit },
            });

            setTickets(data?.data?.docs || []);
            setPage(data?.data?.page || 1);
            setTotal(data?.data?.total || 0);
            setHasNextPage(data?.data?.hasNextPage || false);
            setHasPrevPage(data?.data?.hasPrevPage || false);
        } catch {
            toast.error("خطا در دریافت پیام‌ها");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets(page);
    }, [page]);

    const toggleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handlePrev = () => hasPrevPage && setPage((p) => p - 1);
    const handleNext = () => hasNextPage && setPage((p) => p + 1);

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete("/api/ticket/delete-list-admin", {
                data: { tikcet_ids: selected },
            });
            toast.success("پیام‌ها با موفقیت حذف شدند");
            setShowConfirm(false);
            setSelected([]);
            fetchTickets(page);
        } catch {
            toast.error("خطا در حذف پیام‌ها");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section className="w-full max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6 py-2">
                <h2 className="text-lg md:text-xl font-semibold text-background py-1">پیام‌های کاربران</h2>

                {selected.length > 0 && (
                    <button
                        onClick={() => setShowConfirm(true)}
                        className="flex items-center text-sm gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                        <Trash2 size={18} />
                        حذف {selected.length} پیام
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex flex-col gap-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 animate-pulse">
                            <div className="min-w-5 h-5 rounded-full bg-black/5" />
                            <div className="w-10/12 flex-1 backdrop-blur-sm border border-black/5 rounded-2xl p-4">
                                <div className="flex items-start justify-between flex-wrap gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-black/10" />
                                        <div className="flex flex-col gap-2">
                                            <div className="w-24 h-3 bg-black/10 rounded" />
                                            <div className="w-32 h-2.5 bg-black/10 rounded" />
                                        </div>
                                    </div>
                                    <div className="w-20 h-2.5 bg-black/10 rounded" />
                                </div>
                                <div className="mt-4 pt-3">
                                    <div className="w-full h-3 bg-black/10 rounded mb-2" />
                                    <div className="w-3/4 h-3 bg-black/10 rounded" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : tickets.length === 0 ? (
                <p className="text-background text-center py-16">هیچ پیامی یافت نشد.</p>
            ) : (
                <div className="flex flex-col gap-4">
                    {tickets.map((ticket) => {
                        const isSelected = selected.includes(ticket.ID);
                        return (
                            <div key={ticket.ID} className="flex items-center gap-3">
                                <button
                                    onClick={() => toggleSelect(ticket.ID)}
                                    className={`min-w-5 h-5 flex items-center justify-center rounded-full border transition-colors ${isSelected ? "bg-accent border-accent" : "border-black/30"}`}
                                >
                                    <Check className={`size-4 text-foreground ${isSelected ? "opacity-100" : "opacity-0"}`}/>
                                </button>

                                <div className="w-10/12 flex-1 bg-white/60 backdrop-blur-sm border border-white/20 rounded-2xl p-4 hover:bg-white transition-all">
                                    <div className="flex items-start justify-between flex-wrap gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-orange-200 text-accent rounded-full p-2">
                                                <UserRound size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-background text-base">
                                                    {ticket.FullName || "کاربر ناشناس"}
                                                </h3>
                                                <div className="flex items-center justify-center gap-2 text-black/70">
                                                    {ticket.Type === "mobile" ? (
                                                        <>
                                                            <Phone size={14} className="text-green-500" />
                                                            <span className="text-sm truncate max-w-28 tablet:max-w-max">{ticket.Connection}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Mail size={14} className="text-blue-500" />
                                                            <span dir="ltr" className="text-xs truncate max-w-28 tablet:max-w-max">{ticket.Connection}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <span className="text-xs text-background/60">
                                            {new Date(ticket.CreatedAt).toLocaleDateString("fa-IR", {
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                    </div>

                                    <div className="mt-3 border-t border-white/20 pt-3">
                                        <p className="text-background/90 text-sm leading-relaxed">
                                            {ticket.Title}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && (
                <div className="flex justify-center items-center gap-4 mt-6 flex-wrap">
                    <button
                        onClick={handlePrev}
                        disabled={!hasPrevPage}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-black/20 transition-colors ${hasPrevPage
                                ? "hover:bg-black/10 text-background"
                                : "text-black/40 cursor-not-allowed"
                            }`}
                    >
                        <ChevronRight size={16} />
                        قبلی
                    </button>

                    <span className="text-black/70 text-sm">
                        صفحه {page} از {Math.ceil(total / limit) || 1}
                    </span>

                    <button
                        onClick={handleNext}
                        disabled={!hasNextPage}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-black/20 transition-colors ${hasNextPage
                                ? "hover:bg-black/10 text-background"
                                : "text-black/40 cursor-not-allowed"
                            }`}
                    >
                        بعدی
                        <ChevronLeft size={16} />
                    </button>
                </div>
            )}

            <ConfirmModal
                isOpen={showConfirm}
                onClose={() => !deleting && setShowConfirm(false)}
                onConfirm={confirmDelete}
                title="حذف پیام‌ها"
                description={`آیا از حذف ${selected.length} پیام انتخاب‌شده اطمینان دارید؟`}
                confirmText="بله، حذف شود"
                cancelText="انصراف"
                loading={deleting}
            />
        </section>
    );
}